"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Link from "next/link";
import { Menu } from "lucide-react";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import "./mobile-nav-sheet.css";
interface MobileNavSheetProps {
  /** Sheet heading. */
  title?: string;
  /** Accessible label for the hamburger trigger. */
  triggerLabel?: string;
  /** Menu rows — typically `MobileNavItem`s. */
  children: ReactNode;
  /**
   * Optional badge rendered on the hamburger trigger (e.g. an unread-
   * notification dot), so a signal is visible without opening the menu.
   */
  triggerBadge?: ReactNode;
  /**
   * Breakpoint at which the hamburger trigger hides (the desktop nav takes
   * over). Defaults to `md:hidden`; surfaces whose desktop nav needs more
   * room before it fits (e.g. the reader navbar) override this to a larger
   * breakpoint so the sheet stays through the medium band.
   */
  triggerClassName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Shared hamburger + left slide-out sheet shell for mobile navigation.
 * Owns the trigger (mobile-only via `.mobile-nav__trigger`), the sheet, and
 * the menu container; callers compose the rows. Caller controls open state so
 * it can react to auth/coins fetches.
 */
export function MobileNavSheet(t0) {
  const $ = _c(19);
  const {
    title: t1,
    triggerLabel: t2,
    children,
    triggerBadge,
    triggerClassName: t3,
    open,
    onOpenChange
  } = t0;
  const title = t1 === undefined ? "Menu" : t1;
  const triggerLabel = t2 === undefined ? "Open menu" : t2;
  const triggerClassName = t3 === undefined ? "md:hidden" : t3;
  let t4;
  if ($[0] !== triggerClassName) {
    t4 = cn("mobile-nav__trigger relative inline-flex items-center justify-center", triggerClassName);
    $[0] = triggerClassName;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Menu className="size-6" aria-hidden={true} />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t4 || $[4] !== triggerBadge || $[5] !== triggerLabel) {
    t6 = <SheetTrigger asChild={true}><button type="button" className={t4} aria-label={triggerLabel}>{t5}{triggerBadge}</button></SheetTrigger>;
    $[3] = t4;
    $[4] = triggerBadge;
    $[5] = triggerLabel;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== title) {
    t7 = <SheetHeader className="border-b border-border"><SheetTitle>{title}</SheetTitle></SheetHeader>;
    $[7] = title;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== children) {
    t8 = <nav className="mobile-nav__nav flex flex-col gap-1 px-3">{children}</nav>;
    $[9] = children;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <SheetContent side="left" className="w-[280px] sm:max-w-[280px]">{t7}{t8}</SheetContent>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== onOpenChange || $[15] !== open || $[16] !== t6 || $[17] !== t9) {
    t10 = <Sheet open={open} onOpenChange={onOpenChange}>{t6}{t9}</Sheet>;
    $[14] = onOpenChange;
    $[15] = open;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
interface MobileNavItemProps {
  icon?: ReactNode;
  children: ReactNode;
  /** Renders a link row (wrapped in SheetClose so the sheet closes on tap). */
  href?: string;
  /** Renders a button row. */
  onClick?: () => void;
  active?: boolean;
  danger?: boolean;
  /** Trailing content, e.g. a coins badge. */
  trailing?: ReactNode;
  className?: string;
}

/** A single styled menu row — link (default) or button. */
export function MobileNavItem(t0) {
  const $ = _c(18);
  const {
    icon,
    children,
    href,
    onClick,
    active,
    danger,
    trailing,
    className
  } = t0;
  const t1 = active && "mobile-nav__item--active";
  const t2 = danger && "mobile-nav__item--danger";
  let t3;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2) {
    t3 = cn("mobile-nav__item", t1, t2, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const classes = t3;
  let t4;
  if ($[4] !== children) {
    t4 = <span className="flex-1">{children}</span>;
    $[4] = children;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== icon || $[7] !== t4 || $[8] !== trailing) {
    t5 = <>{icon}{t4}{trailing}</>;
    $[6] = icon;
    $[7] = t4;
    $[8] = trailing;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const body = t5;
  if (href) {
    let t6;
    if ($[10] !== body || $[11] !== classes || $[12] !== href) {
      t6 = <SheetClose asChild={true}><Link href={href} className={classes}>{body}</Link></SheetClose>;
      $[10] = body;
      $[11] = classes;
      $[12] = href;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t6;
  if ($[14] !== body || $[15] !== classes || $[16] !== onClick) {
    t6 = <button type="button" onClick={onClick} className={classes}>{body}</button>;
    $[14] = body;
    $[15] = classes;
    $[16] = onClick;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}